// client/src/pages/admin/AdminServices.js
import { useEffect, useState, useMemo } from "react";
import api from "../../api/axiosInstance";
import { toast } from "react-hot-toast";

export default function AdminServices() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  // FILTER STATES
  const [platform, setPlatform] = useState("ALL");
  const [category, setCategory] = useState("ALL");
  const [status, setStatus] = useState("ALL");
  const [search, setSearch] = useState(""); // ✅ NEW

  const load = async () => {
    try {
      const res = await api.get("/admin/services", {
        params: { platform, category, status, search },
      });
      setServices(res.data || []);
    } catch {
      toast.error("Failed to load services");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [platform, category, status, search]);

  // UNIQUE FILTER OPTIONS
  const platforms = useMemo(
    () => ["ALL", ...new Set(services.map((s) => s.platform).filter(Boolean))],
    [services]
  );

  const categories = useMemo(
    () => ["ALL", ...new Set(services.map((s) => s.category).filter(Boolean))],
    [services]
  );

  const toggleStatus = async (id) => {
    try {
      await api.patch(`/admin/services/${id}/toggle`);
      toast.success("Service updated");
      load();
    } catch {
      toast.error("Toggle failed");
    }
  };

  if (loading) return <div className="text-white">Loading services…</div>;

  return (
    <div className="space-y-6 text-white">
      <h1 className="text-2xl font-bold">Services Control Panel</h1>

      {/* FILTER BAR */}
      <div className="grid md:grid-cols-5 gap-4 bg-white/5 p-4 rounded-xl">
        <Filter
          label="Platform"
          value={platform}
          onChange={setPlatform}
          options={platforms}
        />
        <Filter
          label="Category"
          value={category}
          onChange={setCategory}
          options={categories}
        />
        <Filter
          label="Status"
          value={status}
          onChange={setStatus}
          options={["ALL", "active", "paused"]}
        />

        {/* ✅ SEARCH FIELD */}
        <div>
          <label className="block text-xs text-gray-400 mb-1">
            Search Service
          </label>
          <input
            type="text"
            placeholder="Search by Service ID / Name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-black/30 border border-white/10 text-sm"
          />
        </div>
      </div>

      {/* SERVICES TABLE */}
      <div className="overflow-x-auto rounded-xl border border-white/10">
        <table className="min-w-full text-sm">
          <thead className="bg-white/10 text-gray-300">
            <tr>
              <th className="p-3 text-left">Service ID</th>
              <th className="p-3 text-left">Service Name</th>
              <th className="p-3">Platform</th>
              <th className="p-3">Category</th>
              <th className="p-3">Min</th>
              <th className="p-3">Max</th>
              <th className="p-3">Provider</th>
              <th className="p-3">Provider Cost</th>
              <th className="p-3">Status</th>
              <th className="p-3">Toggle</th>
            </tr>
          </thead>

          <tbody>
            {services.map((s) => (
              <tr
                key={s._id}
                className="border-t border-white/5 hover:bg-white/5"
              >
                <td className="p-3 font-mono">
                  {s.providerServiceId || "—"}
                </td>

                <td className="p-3 max-w-[420px]">
                  <div className="font-medium">{s.name}</div>
                </td>

                <td className="p-3 text-center">{s.platform || "-"}</td>
                <td className="p-3 text-center">{s.category || "-"}</td>
                <td className="p-3 text-center">{s.min}</td>
                <td className="p-3 text-center">{s.max}</td>
                <td className="p-3 text-center">
                  {s.provider?.name || "-"}
                </td>
                <td className="p-3 text-center">
                  ₹{Number(s.providerRate || 0).toFixed(2)}
                </td>

                <td
                  className={`p-3 text-center font-semibold ${
                    s.status === "active"
                      ? "text-green-400"
                      : "text-yellow-400"
                  }`}
                >
                  {s.status}
                </td>

                <td className="p-3 text-center">
                  <button
                    onClick={() => toggleStatus(s._id)}
                    className="px-3 py-1 rounded bg-white/10 hover:bg-white/20"
                  >
                    Toggle
                  </button>
                </td>
              </tr>
            ))}

            {services.length === 0 && (
              <tr>
                <td colSpan="10" className="p-6 text-center text-gray-400">
                  No services found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Filter({ label, value, onChange, options }) {
  return (
    <div>
      <label className="block text-xs text-gray-400 mb-1">{label}</label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-black/40 border border-white/10 rounded px-2 py-2 text-sm"
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}
